/**
 * Port of Angular `validateData` / `getFilterValue` from
 * `resident-application/payment.datahandler.ts` and `transfer-process.component.ts`.
 */

/** Mirrors Angular `payment.datahandler` `dateControl` (subset sufficient for transfer payloads). */
const DATE_CONTROL = new Set<string>([
  "policy_start_date",
  "policy_end_date",
  "merchant_decision_date",
  "liberty_deposit_date",
  "funds_date",
  "funds_withdrawn_date",
  "response_deadline",
  "transaction_date",
  "chargeback_notification_date",
  "effective_date",
  "turn_in_date",
  "how_recent",
  "rent_debt_collection_how_recent",
  "last_eviction",
  "moveIn_date",
  "birthdate",
  "move_in_date",
  "date_unit_is_back_on_market",
  "unit_vacancy",
  "movein_date",
  "moveOut_date",
  "reissue_payment_date",
  "issued_date",
  "resident_last_pay_date",
  "late_date",
  "notice_of_eviction_date",
  "ud_filed_date",
  "default_status_date",
  "lease_start_date",
  "new_resident_lease_start_date",
  "case_date",
  "lease_end_date",
  "version_date",
  "moveout_date",
  "start_date",
  "job_start_date",
  "job_end_date",
  "rental_status_date",
  "adjusted_notice",
  "renter_date",
  "payment_date",
  "first_month_not_paid",
  "adjust_max_movein_date",
  "claim_expiration_date",
  "date_of_birth",
  "effective_start_date",
  "effective_end_date",
])

const AMOUNT_CONTROL = new Set<string>([
  "chargeback_amount",
  "mrg_avg",
  "how_much",
  "rent_debt_collection_how_much",
  "monthly_income",
  "month_payment",
  "rent_amount",
  "income",
  "default_paid",
  "unused_security_deposit",
  "partial_payment",
  "net_amount",
  "resident_last_pay_amt",
  "car_payment",
  "previous_rent",
  "refund_amount",
  "surcharge_fee",
  "payment_amount",
  "partial_payment_amount",
  "sales_rep_rates",
  "account_manager_rates",
  "sales_lead_rates",
  "underwriter_verified_income",
  "eviction_how_much",
  "auto_payment_repossess_how_much",
  "adjustment_amount",
  "user_bonus",
])

const PHONE_KEYS = new Set<string>([
  "mobile_no",
  "supervisor_mobile_no",
  "landlord_mobile_no",
  "contact_phone",
  "company_phone",
  "agent_mobile_no",
  "org_phone",
  "company_office_phone",
  "property_office_phone",
])

function formatDateValue(datevalue: unknown): string {
  if (datevalue == null || datevalue === "") return datevalue as string
  const d = new Date(String(datevalue))
  if (Number.isNaN(d.getTime())) return String(datevalue)
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function validateTransferData(data: unknown): Record<string, unknown> {
  if (data == null || typeof data !== "object") return (data as Record<string, unknown>) ?? {}
  const out = data as Record<string, unknown>
  for (const keyValue of Object.keys(out)) {
    const v = out[keyValue]
    if (v != null && typeof v === "object" && !Array.isArray(v) && !DATE_CONTROL.has(keyValue)) {
      out[keyValue] = validateTransferData(v)
    } else if (v) {
      if (PHONE_KEYS.has(keyValue) && typeof v === "string") {
        out[keyValue] = v.replace(/\D/g, "")
      } else if (DATE_CONTROL.has(keyValue)) {
        out[keyValue] = formatDateValue(v)
      } else if (AMOUNT_CONTROL.has(keyValue)) {
        out[keyValue] = String(v).replace(/[^0-9.]+/g, "")
      }
    } else {
      if (keyValue === "middle_name") {
        delete out[keyValue]
      }
    }
  }
  return out
}

/** Angular `getFilterValue` — trim strings, drop empty. */
export function getFilterValue(params: Record<string, unknown>): Record<string, unknown> {
  const filterParams: Record<string, unknown> = {}
  for (const filterKey of Object.keys(params)) {
    let v = params[filterKey]
    v = typeof v === "string" ? v.trim() : v
    if (v !== "" && v != null) {
      filterParams[filterKey] = v
    }
  }
  return filterParams
}
